<template>
    <transition name="fade-root">
        <div class="flow-navigation">

            <div class="flow-navigation__button">
                <v-btn 
                    v-if="isAdministrator" 
                    variant="plain" 
                    @click="handleSettings">
                    <v-icon icon="fa-solid fa-gear" />
                    <v-tooltip activator="parent" location="start">
                        open settings
                    </v-tooltip>
                </v-btn>
            </div>
            
            <div class="flow-navigation__cards">
                <transition-group name="fade-card">
                    <template 
                        v-if="hasMultipleCards" 
                        v-for="(item, i) in displayItems" 
                        :key="item.index">

                        <!-- If it's a card item, show the button -->
                        <v-btn 
                            v-if="item.type === 'card'" 
                            icon 
                            variant="plain" 
                            class="flow-navigation__card"
                            :class="{ 'active': item.index === activeIndex }"
                            @click="handleSelect(item.index)">
                            <v-icon :icon="item.index === activeIndex
                                ? 'fa-solid fa-square'
                                : 'fa-regular fa-square'" />
                            <v-tooltip activator="parent" location="start">
                                {{ cards[item.index].getDescription() }}
                            </v-tooltip>
                        </v-btn>

                        <!-- Otherwise it's a gap item -->
                        <span 
                            v-else-if="item.type === 'gap'" 
                            class="flow-navigation__gap">
                            <v-icon class="fa-solid fa-ellipsis"></v-icon>
                        </span>

                    </template>
                </transition-group>
            </div>

            <div class="flow-navigation__button">
                <!-- v-if="hasChangedCards" -->
                <v-btn 
                    color="color1" 
                    variant="tonal"
                    elevation="0" 
                    @click="handleReset">
                    <v-icon icon="fa-solid fa-plus" />
                    <v-tooltip activator="parent" location="start">
                        create new flow
                    </v-tooltip>
                </v-btn>
            </div>

        </div>
    </transition>
</template>

<script>
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'

export default {
    name: 'Navigation',
    props: {
        app: {
            type: String,
            required: true
        }
    },
    computed: {
        ...mapGetters('auth', [
            'isAdministrator'
        ]),
		...mapGetters('flows', [
            'hasChanges',
            'hasMultiple',
			'getCards',
			'getActiveIndex'
		]),

        hasChangedCards() {
            return this.hasChanges(this.app)
        },
        hasMultipleCards() {
            return this.hasMultiple(this.app)
        },

        cards() {
            return this.getCards(this.app)
        },
        activeIndex() {
            return this.getActiveIndex(this.app)
        },

        displayItems() {
            if (!this.cards || this.cards.length === 0) return []

            // total number of cards
            const count = this.cards.length
            
            // if total <= 9, show them all with no gaps needed
            if (count <= 9) {
                return this.buildItems([...Array(count).keys()])
            }

            // otherwise, define the first and last indices
            const firstIndices = [0, 1]
            const lastIndices = [count - 2, count - 1]

            // find the best window of cards around the active index
            for (let windowSize = 5; windowSize >= 1; windowSize--) 
            {
                // calculate middle indices
                const midIndices = this.calcMiddleWindow(count, this.activeIndex, windowSize)
                const fullSet = new Set([
                    ...firstIndices,
                    ...midIndices,
                    ...lastIndices
                ])
                // sort them
                const sorted = Array.from(fullSet).sort((a, b) => a - b)

                // count how many gaps would be needed
                const gapCount = this.countGaps(sorted)
                const totalItems = sorted.length + gapCount

                // if total is <= 9, we're good: build and return
                if (totalItems <= 9) {
                    return this.buildItems(sorted)
                }
                // otherwise, we try the next smaller window size
            }

            // fallback handling
            const fallbackSet = new Set([...firstIndices, ...lastIndices])
            const fallbackSorted = Array.from(fallbackSet).sort((a, b) => a - b)
            return this.buildItems(fallbackSorted)
        }
    },

    methods: {
        ...mapActions('dialogs', [
            'open',
        ]),
        ...mapActions('flows', [
            'newFlow',
            'setActiveIndex'
        ]),

        handleReset() {
            this.open({
				component: "restart",
				payload: {
                    app: this.app
				}
			})
        },
        handleSettings() {
            this.open({
                component: 'settings'
            })
        },
        handleSelect(index) {
            this.setActiveIndex({
                index,
                app: this.app
            })
        },

        countGaps(sortedIndices) 
        {
            let gaps = 0
            for (let i = 0; i < sortedIndices.length - 1; i++) {
                if (sortedIndices[i + 1] !== sortedIndices[i] + 1) {
                    gaps++
                }
            }
            return gaps
        },
        calcMiddleWindow(count, activeIndex, windowSize) {
            const minStart = 2
            const maxStart = (count - 3) - (windowSize - 1)
            if (minStart > maxStart) { 
                const fullMiddle = []
                for (let i = 2; i <= count - 3; i++) {
                    fullMiddle.push(i)
                }
                return fullMiddle
            }
            let idealStart = activeIndex - Math.floor((windowSize - 1) / 2)
            let start = Math.max(minStart, Math.min(idealStart, maxStart))
            const result = []
            for (let i = 0; i < windowSize; i++) {
                result.push(start + i)
            }
            return result
        },
        buildItems(sortedIndices) 
        {
            const items = []
            for (let i = 0; i < sortedIndices.length; i++) {
                items.push({ type: 'card', index: sortedIndices[i] })
                if (i < sortedIndices.length - 1) {
                    const curr = sortedIndices[i]
                    const next = sortedIndices[i + 1]
                    if (next !== curr + 1) {
                        items.push({ type: 'gap' })
                    }
                }
            }
            return items
        }
    }
}
</script>

<style scoped>
.fade-root-enter-active,
.fade-root-leave-active {
    transition: opacity 0.5s ease;
}

.fade-root-enter-from,
.fade-root-leave-to {
    opacity: 0;
}

.fade-card-enter-active,
.fade-card-leave-active {
    transition: all 0.5s ease;
}

.fade-card-enter-from {
    opacity: 0;
    transform: translateY(5px);
}

.fade-card-leave-to {
    opacity: 0;
    transform: translateY(-5px);
}

.fade-card-move {
    transition: transform 0.3s ease;
}


.flow-navigation {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
}

.flow-navigation__button {
    width: 100%;  
    height: 64px;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: all;
}

.flow-navigation .v-btn {
    min-height: 54px;
    max-height: 54px;
    max-width: 54px;
    min-width: 54px;
    padding: 0px;
}

.flow-navigation__cards {
    flex-grow: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;
}

.flow-navigation__cards .v-icon {
    font-size: 1rem;
}

.flow-navigation__card {
    color: rgb(var(--v-theme-secondary));
    pointer-events: all;
}

.flow-navigation__card.active {
    color: rgb(var(--v-theme-primary));
}

.flow-navigation__gap {
    min-height: 48px;
    font-size: 1.2rem;
    user-select: none;
    display: flex;
    justify-content: center;
    align-items: center;
    color: rgb(var(--v-theme-secondary));
}

@media (max-width: 1600px) {
    .flow-navigation__button {
        height: 60px;
    }

    .flow-navigation .v-btn {
        min-height: 50px;
        max-height: 50px;
        max-width: 50px;
        min-width: 50px;
    }
}

@media (max-width: 1200px) {
    .flow-navigation__button {
        height: 50px;
    }

    .flow-navigation .v-btn {
        min-height: 40px;
        max-height: 40px;
        max-width: 40px;
        min-width: 40px;
    }
}
</style>